import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import apiClient from '../api/client'
import type { JobState } from '../api/types'

interface RunResponse {
  id: string
  research_question_id: string
  collection_name: string
  status: JobState
  min_iterations: number
  max_iterations: number
  iterations_executed: number
  created_at: string
  updated_at: string
}

interface QuestionResponse {
  id: string
  question_text: string
}

function StatusBadge({ state }: { state: JobState }): JSX.Element {
  return <span className={`badge badge--${state}`}>{state.replace(/_/g, ' ')}</span>
}

function DashboardPage(): JSX.Element {
  const [runs, setRuns] = useState<RunResponse[]>([])
  const [questions, setQuestions] = useState<Map<string, string>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      const [runsRes, qRes] = await Promise.all([
        apiClient.get<RunResponse[]>('/research/runs'),
        apiClient.get<QuestionResponse[]>('/research/questions'),
      ])
      const runsData = runsRes.data as unknown as RunResponse[]
      const qData = qRes.data as unknown as QuestionResponse[]
      setRuns(runsData)
      const qMap = new Map<string, string>()
      qData.forEach((q) => qMap.set(q.id, q.question_text))
      setQuestions(qMap)
      setError(null)
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      setError(maybeAxios?.response?.data?.detail ?? 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 5000)
    return () => clearInterval(interval)
  }, [])

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Research Dashboard</h1>
          <Link to="/research/new" className="page-action">New Research</Link>
        </div>
        <p>Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Research Dashboard</h1>
          <Link to="/research/new" className="page-action">New Research</Link>
        </div>
        <p style={{ color: '#c00' }}>{error}</p>
        <button className="page-action-secondary" onClick={fetchData}>Retry</button>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Research Dashboard</h1>
        <Link to="/research/new" className="page-action">New Research</Link>
      </div>
      {runs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
          <p>No research runs yet.</p>
          <Link to="/research/new" className="page-action">Start your first research</Link>
        </div>
      ) : (
        <div className="runs-list">
          {runs.map((run) => {
            const questionText = questions.get(run.research_question_id) ?? run.research_question_id.slice(0, 8)
            return (
              <div key={run.id} className="run-card">
                <div className="run-card-header">
                  <Link to={`/runs/${run.id}`} className="run-question" title={questionText}>
                    {questionText.length > 120 ? questionText.slice(0, 120) + '...' : questionText}
                  </Link>
                  <StatusBadge state={run.status} />
                </div>
                <div className="run-card-meta">
                  <span>Iteration {run.iterations_executed} / {run.max_iterations} (min {run.min_iterations})</span>
                  <span>Collection {run.collection_name}</span>
                  <span>{new Date(run.updated_at).toLocaleString()}</span>
                </div>
                {run.status === 'waiting_for_user' && (
                  <div className="run-card-alert">
                    <Link to={`/runs/${run.id}/review`} className="run-review-link">Review queries</Link>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default DashboardPage
